/**
 * Selective dynamic-config-protected wrapper for non-essential Rovo Chat localStorage access.
 *
 * Background (HOT-303866): a corrupt value in a localStorage key broke Rovo Chat
 * for ~23,000 users. During the incident, there was no way to quickly disable the
 * code reading that key, so the fix took days to ship.
 *
 * When `rovo_chat_disabled_nonessential_ls_keys` is enabled, this wrapper checks
 * the Statsig dynamic config `rovo_chat_disabled_non_essential_ls_keys` before
 * executing the provided callback. The config value is a `string[]` of localStorage key names that are currently
 * disabled. If the given `key` appears in that list, the callback is skipped and `null`
 * is returned. If the key is not in the list (default), the callback is executed normally.
 *
 * This allows individual localStorage keys to be selectively disabled during an incident
 * without affecting others — no code deploy required.
 *
 * ```ts
 * import { withNonEssentialLocalStorage } from
 *   '@atlassian/conversation-assistant-utils/nonEssentialLocalStorage/withNonEssentialLocalStorage';
 *
 * // Read
 * const value = withNonEssentialLocalStorage(
 *   'aiMateChatLoadPreference',
 *   () => localStorage.getItem(getChatLoadPreferenceKey(siteId))
 * );
 *
 * // Write (return value ignored)
 * withNonEssentialLocalStorage(
 *   'aiMateChatLoadPreference',
 *   () => localStorage.setItem(getChatLoadPreferenceKey(siteId), conversationId)
 * );
 * ```
 */

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { NON_ESSENTIAL_LOCAL_STORAGE_KEYS } from './nonEssentialLocalStorageKeys';

const DYNAMIC_CONFIG_NAME = 'rovo_chat_disabled_non_essential_ls_keys';
const DYNAMIC_CONFIG_PARAM = 'value';

export const withNonEssentialLocalStorage = <T>(
	key: (typeof NON_ESSENTIAL_LOCAL_STORAGE_KEYS)[number],
	callback: () => T,
): T | null => {
	if (!fg('rovo_chat_disabled_nonessential_ls_keys')) {
		return callback();
	}

	const disabledKeys = FeatureGates.getExperimentValue<string[]>(
		DYNAMIC_CONFIG_NAME,
		DYNAMIC_CONFIG_PARAM,
		[],
		{ fireExperimentExposure: false },
	);

	if (Array.isArray(disabledKeys) && disabledKeys.includes(key)) {
		return null;
	}
	return callback();
};
